import { plataforma } from '@/platform';
import type { Metodo } from '@/platform';
import { HttpError } from '@/shared/api/httpClient';
import type {
  AlvoLeitura,
  FotoCapturaDTO,
  LeituraImagemDTO,
  ModoCaptura,
  SessaoCapturaDTO,
} from '@/features/captura/tipos';

/**
 * Captura pelo celular. Fotos e sessões moram só no servidor: o desktop
 * chama o servidor online pelo main process (IPC → `capturaService`) e o
 * web chama `/app/api/captura/...`. Os caminhos abaixo são os mesmos nos
 * dois alvos — quem resolve é a `plataforma`.
 *
 * Não usa o `httpClient` direto por um detalhe: no web, falha de rede volta
 * como `status: 0` com `erro`, e aqui isso precisa virar exceção (a captura
 * não tem "modo offline" para cair de volta).
 */
async function chamar<T>(
  metodo: Metodo,
  caminho: string,
  corpo?: unknown,
  query?: Record<string, string>,
): Promise<T> {
  const resposta = await plataforma.request({ metodo, caminho, corpo, query });
  if ('erro' in resposta && resposta.erro) {
    throw new HttpError(resposta.status, resposta.erro);
  }
  if (resposta.status === 0 || resposta.status >= 400) {
    throw new HttpError(resposta.status, null);
  }
  if (resposta.status === 204) return undefined as T;
  return ('corpo' in resposta ? resposta.corpo : undefined) as T;
}

export type CriarSessaoCapturaEntrada = {
  modo: ModoCaptura;
  orcamentoId?: string | null;
  placa?: string | null;
  numero?: string | null;
};

export type FiltroFotosCaptura = { orcamentoId: string } | { sessaoId: string };

const seg = (v: string) => encodeURIComponent(v);

export const capturaApi = {
  criarSessao: (entrada: CriarSessaoCapturaEntrada) =>
    chamar<SessaoCapturaDTO>('POST', '/api/captura/sessoes', {
      modo: entrada.modo,
      orcamentoId: entrada.orcamentoId ?? null,
      placa: entrada.placa ?? null,
      numero: entrada.numero ?? null,
    }),

  encerrarSessao: (id: string) => chamar<void>('DELETE', `/api/captura/sessoes/${seg(id)}`),

  /** Fotos da sessão ainda sem O.S. passam a ser da O.S. recém-salva. */
  vincularSessao: (id: string, orcamentoId: string) =>
    chamar<{ vinculadas: number }>('POST', `/api/captura/sessoes/${seg(id)}/vincular`, {
      orcamentoId,
    }),

  listarFotos: (filtro: FiltroFotosCaptura) =>
    chamar<FotoCapturaDTO[]>(
      'GET',
      '/api/captura/fotos',
      undefined,
      'orcamentoId' in filtro ? { orcamentoId: filtro.orcamentoId } : { sessaoId: filtro.sessaoId },
    ),

  removerFoto: (id: string) => chamar<void>('DELETE', `/api/captura/fotos/${seg(id)}`),

  /** OCR (Tesseract no servidor). `imagemBase64` sem o prefixo `data:`. */
  lerImagem: (entrada: { alvo: AlvoLeitura; imagemBase64: string }) =>
    chamar<LeituraImagemDTO>('POST', '/api/captura/ler-imagem', {
      alvo: entrada.alvo,
      imagemBase64: entrada.imagemBase64,
    }),
};

export const MENSAGEM_SEM_INTERNET =
  'Sem conexão com o servidor. A captura pelo celular precisa de internet — verifique a conexão e tente de novo.';

/** A mensagem do erro é a genérica que a ponte monta quando o servidor não explicou nada. */
function mensagemGenerica(m: string | undefined): boolean {
  return !m || /^Falha na (operação|requisição)/i.test(m);
}

/**
 * Texto amigável para um erro da captura. Falta de rede (web: status 0;
 * desktop: `Indisponivel` vindo do service) vira sempre a mesma frase
 * clara. `porStatus` permite trocar a mensagem genérica por uma do
 * contexto (ex.: 503 no OCR = leitura automática indisponível).
 */
export function mensagemErroCaptura(
  erro: unknown,
  padrao: string,
  porStatus: Partial<Record<number, string>> = {},
): string {
  if (erro instanceof HttpError) {
    if (erro.status === 0) return MENSAGEM_SEM_INTERNET;
    const msg = erro.payload?.mensagem;
    const doContexto = porStatus[erro.status];
    // Status com significado próprio no contexto (413, 429, 503 do OCR…):
    // a explicação do servidor ganha quando existe; senão, a do contexto.
    if (doContexto) return mensagemGenerica(msg) ? doContexto : (msg as string);
    if (erro.payload?.tipo === 'Indisponivel') return MENSAGEM_SEM_INTERNET;
    return mensagemGenerica(msg) ? padrao : (msg as string);
  }
  if (erro instanceof Error && erro.message && !/fetch|network/i.test(erro.message)) {
    return erro.message;
  }
  return padrao;
}
